import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ReceiptText, Banknote, X, AlertTriangle, Search, CheckCircle2, Clock, Printer, FileText } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const PendingPaymentInvoices = ({ user }) => {
  // --- Tabs / data ---
  const [activeTab, setActiveTab] = useState('cobradas'); // 'cobradas' | 'pendientes'
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [paidLoading, setPaidLoading] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  const [currentRegister, setCurrentRegister] = useState(null);
  const [businessConfig, setBusinessConfig] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [ncfTypes, setNcfTypes] = useState([]);
  const [products, setProducts] = useState([]);

  // --- Cobro dialog ---
  const [cobroDialogOpen, setCobroDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [cobroFormData, setCobroFormData] = useState({
    tipo_pago: 'efectivo',
    es_credito: false,
    dias_credito: 30,
    cliente_id: '',
    monto_recibido: '',
    tipo_ncf: 'B02',
    cliente_nombre: '',
    cliente_rnc: '',
  });
  const [processingCobro, setProcessingCobro] = useState(false);

  // --- RNC lookup (Facturazo) ---
  const [rncLookupLoading, setRncLookupLoading] = useState(false);
  const [rncPreview, setRncPreview] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetchPaidInvoices();
    fetchPendingInvoices();
    fetchCurrentRegister();
    fetchBusinessConfig();
    fetchCustomers();
    fetchNCFTypes();
    fetchProducts();
  }, [user]);

  const fetchPaidInvoices = async () => {
    try {
      setPaidLoading(true);
      const response = await axios.get(`${API}/invoices/paid-from-pending`);
      setPaidInvoices(response.data || []);
    } catch (error) {
      console.error('Error al cargar facturas cobradas');
    } finally {
      setPaidLoading(false);
    }
  };

  const fetchPendingInvoices = async () => {
    try {
      setPendingLoading(true);
      const response = await axios.get(`${API}/invoices/pending-payment`);
      setPendingInvoices(response.data || []);
    } catch (error) {
      console.error('Error al cargar facturas pendientes');
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchCurrentRegister = async () => {
    try {
      const response = await axios.get(`${API}/cash-register/current`);
      setCurrentRegister(response.data);
    } catch (error) {
      // no caja abierta
    }
  };

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setBusinessConfig(response.data);
    } catch (error) { /* silent */ }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setCustomers((response.data || []).filter(c => c.is_active));
    } catch (error) { /* silent */ }
  };

  const fetchNCFTypes = async () => {
    try {
      const response = await axios.get(`${API}/ncf-types/sales`);
      setNcfTypes(response.data || []);
    } catch (error) {
      setNcfTypes([
        { code: 'B02', description: 'Consumidor Final' },
        { code: 'B01', description: 'Crédito Fiscal' },
      ]);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data || []);
    } catch (error) { /* silent */ }
  };

  const openCobroDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setRncPreview(null);
    setCobroFormData({
      tipo_pago: 'efectivo',
      es_credito: false,
      dias_credito: 30,
      cliente_id: invoice.cliente_id || '',
      monto_recibido: String(invoice.total || ''),
      tipo_ncf: invoice.tipo_ncf || 'B02',
      cliente_nombre: invoice.cliente?.nombre || '',
      cliente_rnc: invoice.cliente?.rnc || '',
    });
    setCobroDialogOpen(true);
  };

  const closeCobroDialog = () => {
    setCobroDialogOpen(false);
    setSelectedInvoice(null);
    setRncPreview(null);
  };

  // --- RNC Lookup (Facturazo) ---
  const handleRncLookup = async () => {
    const rnc = (cobroFormData.cliente_rnc || '').trim().replace(/-/g, '');
    if (!rnc || rnc.length < 9) {
      toast.error('Ingrese un RNC válido (mínimo 9 dígitos)');
      return;
    }
    setRncLookupLoading(true);
    setRncPreview(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/rnc/lookup`, {
        params: { rnc },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.found) {
        setRncPreview({ name: res.data.name, status: res.data.status });
      } else {
        toast.error('RNC no encontrado');
      }
    } catch (err) {
      toast.error('Error al consultar RNC');
    } finally {
      setRncLookupLoading(false);
    }
  };

  const confirmRncName = () => {
    if (!rncPreview) return;
    setCobroFormData(prev => ({
      ...prev,
      cliente_nombre: rncPreview.name,
      tipo_ncf: rncPreview.status === 'ACTIVO' ? 'B01' : prev.tipo_ncf,
    }));
    toast.success(
      rncPreview.status === 'ACTIVO'
        ? 'Cliente actualizado y NCF cambiado a B01 (Crédito Fiscal)'
        : 'Nombre del cliente actualizado'
    );
    setRncPreview(null);
  };

  const printInvoice = (invoice) => {
    const config = {
      logo: businessConfig?.logo || '',
      nombre_comercio: businessConfig?.nombre_comercio || businessConfig?.sistema_nombre || 'COMERCIO',
      sistema_nombre: businessConfig?.sistema_nombre || 'Sistema',
      rnc: businessConfig?.rnc || '',
      telefono: businessConfig?.telefono || '',
      direccion: businessConfig?.direccion || '',
      mensaje_factura: businessConfig?.mensaje_factura || ''
    };

    // Mapa de descripción de NCF -> Título de la factura
    const ncfTitleMap = {
      'B01': 'FACTURA DE CREDITO FISCAL',
      'B02': 'FACTURA DE CONSUMO',
      'B03': 'NOTA DE DEBITO',
      'B04': 'NOTA DE CREDITO',
      'B14': 'FACTURA REGIMEN ESPECIAL',
      'B15': 'FACTURA GUBERNAMENTAL',
    };
    const tituloFactura = ncfTitleMap[invoice.tipo_ncf] || 'FACTURA';

    const formaPagoLabel = (tp) => {
      const map = {
        efectivo: 'Efectivo',
        tarjeta: 'Tarjeta de Credito',
        transferencia: 'Transferencia',
        cheque: 'Cheque',
        credito: 'Credito',
        credito_favor: 'Nota de Credito'
      };
      return map[tp] || (tp ? tp.toString().charAt(0).toUpperCase() + tp.toString().slice(1) : 'Efectivo');
    };

    const formatPrintDate = (dateStr) => {
      if (!dateStr) {
        const now = new Date();
        return `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')}/${now.getFullYear()}`;
      }
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        return dateStr.split(' ')[0];
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    };

    const formatPrintTime = (dateStr) => {
      const fmt = (d) => {
        let h = d.getHours();
        const m = String(d.getMinutes()).padStart(2,'0');
        const ampm = h >= 12 ? 'p. m.' : 'a. m.';
        h = h % 12; h = h ? h : 12;
        return `${String(h).padStart(2,'0')}:${m} ${ampm}`;
      };
      if (!dateStr) return fmt(new Date());
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const parts = dateStr.split(' ');
        if (parts.length >= 3) {
          const t = parts[1].split(':').slice(0, 2).join(':');
          const ampm = parts[2].toLowerCase() === 'pm' ? 'p. m.' : 'a. m.';
          return `${t} ${ampm}`;
        }
      }
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? fmt(new Date()) : fmt(d);
    };

    const numeroFactura = invoice.numero_factura || (invoice.id ? invoice.id.slice(0, 8) : '');
    const cajeroNombre = invoice.cobrado_por_nombre || invoice.cajero_nombre || user?.full_name || '';
    const barcodeData = encodeURIComponent(numeroFactura);
    const barcodeUrl = `https://barcode.tec-it.com/barcode.ashx?data=${barcodeData}&code=Code128&dpi=96&translate-esc=true`;

    // Fecha de vencimiento del NCF
    const ncfInfo = (ncfTypes || []).find(t => t.code === invoice.tipo_ncf);
    let validoHasta = '';
    if (ncfInfo?.fecha_vencimiento) {
      const fv = new Date(ncfInfo.fecha_vencimiento);
      if (!isNaN(fv.getTime())) {
        validoHasta = `${String(fv.getDate()).padStart(2,'0')}/${String(fv.getMonth()+1).padStart(2,'0')}/${fv.getFullYear()}`;
      }
    }

    const productMap = new Map((products || []).map(p => [p.id, p]));
    const tasaItbis = (businessConfig?.itbis_porcentaje ?? 18) / 100;
    const itbisHabilitado = businessConfig?.itbis_habilitado !== false;

    const w = window.open('', '', 'width=320,height=720');
    w.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Factura ${numeroFactura}</title>
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            @page { size: 80mm auto; margin: 2mm; }
            @media print {
              body { margin:0 !important; padding:0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
            body { font-family: 'Courier New', 'Courier', monospace; font-size: 12px; line-height: 1.35; width: 76mm; margin: 0 auto; padding: 3mm; color: #000; background: #fff; }
            .bold { font-weight: bold; }
            .store-name { font-size: 18px; font-weight: bold; letter-spacing: 1px; text-align: center; margin: 1mm 0 2mm 0; }
            .header-line { text-align: center; font-size: 12px; line-height: 1.3; margin: 0.3mm 0; }
            .logo { max-width: 45mm; max-height: 18mm; margin: 0 auto 2mm; display: block; }
            .sep { border-top: 1px dashed #000; margin: 2.5mm 0; height: 0; }
            .row { display: flex; justify-content: space-between; align-items: flex-start; gap: 4mm; margin: 0.6mm 0; font-size: 12px; line-height: 1.35; }
            .row .label { white-space: nowrap; }
            .row .value { text-align: right; word-break: break-word; }
            .doc-title { text-align: center; font-size: 13px; font-weight: bold; margin: 2mm 0; letter-spacing: 0.4px; }
            .items-header { display: flex; justify-content: space-between; font-weight: bold; font-size: 12px; padding: 0.5mm 0; }
            .items-header .col-desc { flex: 2.6; text-align: left; }
            .items-header .col-itbis { flex: 1; text-align: right; padding-right: 1mm; }
            .items-header .col-neto { flex: 1.2; text-align: right; }
            .items-header .col-pct { flex: 0.4; text-align: right; }
            .item-block { margin: 1.5mm 0; }
            .item-line2 { font-size: 12px; font-weight: bold; line-height: 1.3; }
            .item-detail { display: flex; justify-content: space-between; font-size: 11px; margin-top: 0.3mm; line-height: 1.3; }
            .item-detail .col-qtyprice { flex: 2.6; text-align: left; }
            .item-detail .col-itbis { flex: 1; text-align: right; padding-right: 1mm; }
            .item-detail .col-neto { flex: 1.2; text-align: right; }
            .item-detail .col-pct { flex: 0.4; text-align: right; }
            .barcode-wrap { text-align: center; margin: 3mm 0 1mm 0; }
            .barcode-wrap img { width: 55mm; height: 14mm; display: block; margin: 0 auto; }
            .footer-msg { text-align: center; font-size: 11px; line-height: 1.4; margin: 1mm 0; }
          </style>
        </head>
        <body>
          ${config.logo ? `<img src="${config.logo}" alt="Logo" class="logo" />` : ''}
          <div class="store-name">${(config.nombre_comercio || config.sistema_nombre).toUpperCase()}</div>
          ${config.direccion ? `<div class="header-line">${config.direccion}</div>` : ''}
          ${config.telefono ? `<div class="header-line">Teléfono: ${config.telefono}</div>` : ''}
          ${config.rnc ? `<div class="header-line">RNC: ${config.rnc}</div>` : ''}

          <div class="sep"></div>

          <div class="row"><span class="label">Fecha:</span><span class="value">${formatPrintDate(invoice.fecha)} ${formatPrintTime(invoice.fecha)}</span></div>
          <div class="row"><span class="label">NCF:</span><span class="value bold">${invoice.ncf || 'N/A'}</span></div>
          ${validoHasta ? `<div class="row"><span class="label">Valido Hasta:</span><span class="value">${validoHasta}</span></div>` : ''}

          <div class="sep"></div>

          <div class="row"><span class="label">Factura #:</span><span class="value bold">${numeroFactura}</span></div>
          <div class="row"><span class="label">Cliente:</span><span class="value">${(invoice.cliente?.nombre || 'CONSUMIDOR FINAL').toUpperCase()}</span></div>
          ${(invoice.cliente?.rnc) && invoice.cliente.rnc !== '000000000' && invoice.cliente.rnc !== '000-0000000-0' ? `
            <div class="row"><span class="label">RNC/CED:</span><span class="value">${invoice.cliente.rnc}</span></div>
          ` : ''}

          <div class="doc-title">${tituloFactura}</div>

          <div class="sep"></div>

          <div class="items-header">
            <span class="col-desc">DESCRIPCION</span>
            <span class="col-itbis">ITBIS</span>
            <span class="col-neto">NETO</span>
            <span class="col-pct">%</span>
          </div>

          <div class="sep"></div>

          ${(invoice.items || []).map(item => {
            const precioFinal = item.precio_unitario || item.precio || 0;
            const prod = productMap.get(item.producto_id);
            const aplicaItbis = itbisHabilitado && (prod ? prod.aplica_itbis !== false : true);
            const itbisItem = aplicaItbis ? Number((item.subtotal - (item.subtotal / (1 + tasaItbis))).toFixed(2)) : 0;
            const netoItem = aplicaItbis ? Number((item.subtotal / (1 + tasaItbis)).toFixed(2)) : item.subtotal;
            const pctTax = aplicaItbis ? `${Math.round(tasaItbis * 100)}` : 'E.';
            const codigoInterno = prod?.codigo_interno || item.codigo_interno || '';
            const nombreProducto = (item.producto_nombre || item.nombre || '').toUpperCase();
            const descripcion = codigoInterno ? `${codigoInterno} - ${nombreProducto}` : nombreProducto;

            return `
              <div class="item-block">
                <div class="item-line2">${descripcion}</div>
                <div class="item-detail">
                  <span class="col-qtyprice">${item.cantidad} x $${precioFinal.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  <span class="col-itbis">$${itbisItem.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  <span class="col-neto">$${netoItem.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  <span class="col-pct">${pctTax}</span>
                </div>
              </div>
            `;
          }).join('')}

          <div class="sep"></div>

          <div class="row"><span class="label">Sub Total</span><span class="value">$${(invoice.subtotal || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
          ${(invoice.itbis || 0) > 0 ? `<div class="row"><span class="label">ITBIS (18%)</span><span class="value">$${invoice.itbis.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>` : ''}
          <div class="row bold" style="font-size:13px;"><span class="label">Total</span><span class="value">$${(invoice.total || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>

          <div class="sep"></div>

          ${invoice.es_credito ? `
            <div class="row"><span class="label">Crédito (${invoice.dias_credito || 30} días)</span><span class="value">$${(invoice.total || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
          ` : (
            (invoice.formas_pago && invoice.formas_pago.length > 0) ?
              invoice.formas_pago.map(fp => `
                <div class="row"><span class="label">${formaPagoLabel(fp.tipo_pago)}</span><span class="value">$${(fp.monto || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
              `).join('') :
              `<div class="row"><span class="label">${formaPagoLabel(invoice.tipo_pago)}</span><span class="value">$${(invoice.total || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>`
          )}
          ${invoice.tipo_pago === 'efectivo' && invoice.monto_recibido ? `
            <div class="row"><span class="label">Recibido</span><span class="value">$${(invoice.monto_recibido || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
            <div class="row"><span class="label">Cambio</span><span class="value">$${(invoice.cambio || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
          ` : `
            <div class="row"><span class="label">Cambio Pendiente</span><span class="value">$0.00</span></div>
          `}

          <div class="barcode-wrap">
            <img src="${barcodeUrl}" alt="Barcode" onerror="this.style.display='none'" />
          </div>

          <div class="sep"></div>

          <div class="row"><span class="label">Transaccion #:</span><span class="value">${numeroFactura}</span></div>
          <div class="row"><span class="label">Cajero:</span><span class="value">${cajeroNombre}</span></div>

          <div class="sep"></div>

          ${config.mensaje_factura ? `<div class="footer-msg">${config.mensaje_factura.replace(/\n/g, '<br/>')}</div>` : ''}

          <script>window.onload=function(){setTimeout(function(){window.print();setTimeout(function(){window.close();},150);},350);}</script>
        </body>
      </html>
    `);
    w.document.close();
  };

  const handleCompleteCobro = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    if (cobroFormData.es_credito) {
      const cust = customers.find(c => c.id === cobroFormData.cliente_id);
      if (!cust) {
        toast.error('Para ventas a crédito debe seleccionar un cliente registrado');
        return;
      }
      if (cust.codigo_cliente === 'CLI-00000001') {
        toast.error('El Consumidor Final no puede recibir crédito. Seleccione un cliente con crédito aprobado.');
        return;
      }
      if (!cust.tiene_credito) {
        toast.error(`El cliente ${cust.nombre} no tiene crédito aprobado`);
        return;
      }
      const total = selectedInvoice.total || 0;
      if ((cust.credito_disponible || 0) < total) {
        toast.error(`Crédito insuficiente. Disponible: ${formatCurrency(cust.credito_disponible || 0)}, Requerido: ${formatCurrency(total)}`);
        return;
      }
    }
    // Si el NCF es B01 (Crédito Fiscal) se requiere RNC del cliente
    if (cobroFormData.tipo_ncf === 'B01') {
      const rncClean = (cobroFormData.cliente_rnc || '').replace(/-/g, '').trim();
      if (!rncClean || rncClean.length < 9) {
        toast.error('NCF B01 (Crédito Fiscal) requiere un RNC válido del cliente');
        return;
      }
    }
    const total = selectedInvoice.total || 0;
    const monto = parseFloat(cobroFormData.monto_recibido || '0') || 0;
    if (!cobroFormData.es_credito && cobroFormData.tipo_pago === 'efectivo' && monto < total) {
      toast.error(`El monto recibido (${formatCurrency(monto)}) es menor al total (${formatCurrency(total)})`);
      return;
    }

    try {
      setProcessingCobro(true);
      const payload = {
        tipo_pago: cobroFormData.es_credito ? 'credito' : cobroFormData.tipo_pago,
        formas_pago: cobroFormData.es_credito ? [] : [
          { tipo_pago: cobroFormData.tipo_pago, monto: total }
        ],
        monto_recibido: cobroFormData.tipo_pago === 'efectivo' ? monto : 0,
        cambio: cobroFormData.tipo_pago === 'efectivo' ? Math.max(0, monto - total) : 0,
        es_credito: cobroFormData.es_credito,
        dias_credito: parseInt(cobroFormData.dias_credito || 30),
        cliente_id: cobroFormData.cliente_id || null,
        tipo_ncf: cobroFormData.tipo_ncf,
        cliente_nombre: cobroFormData.cliente_nombre || null,
        cliente_rnc: cobroFormData.cliente_rnc || null,
      };

      const resp = await axios.post(
        `${API}/invoices/${selectedInvoice.id}/complete-payment`,
        payload
      );
      const completed = resp.data;
      toast.success(`Cobro completado - Factura ${completed.numero_factura}`);
      closeCobroDialog();
      fetchPaidInvoices();
      fetchPendingInvoices();
      fetchCurrentRegister();
      setTimeout(() => printInvoice(completed), 500);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data?.detail || 'La factura ya fue procesada por otra caja');
        closeCobroDialog();
        fetchPendingInvoices();
      } else {
        toast.error(err.response?.data?.detail || 'Error al procesar el cobro');
      }
    } finally {
      setProcessingCobro(false);
    }
  };

  const handleCancelPending = async (invoice) => {
    if (!window.confirm(`¿Cancelar la factura pendiente ${invoice.numero_factura}? Se restaurará el stock reservado.`)) return;
    try {
      await axios.post(`${API}/invoices/${invoice.id}/cancel-pending`);
      toast.success('Factura pendiente cancelada');
      fetchPendingInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cancelar factura');
    }
  };

  const goToPendientes = () => {
    setActiveTab('pendientes');
    fetchPendingInvoices();
  };
  const goToCobradas = () => {
    setActiveTab('cobradas');
    fetchPaidInvoices();
  };

  return (
    <div className="space-y-6" data-testid="pending-payment-invoices-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 flex items-center gap-3">
            <ReceiptText className="text-amber-600" size={36} />
            Cobro de Facturas
          </h1>
          <p className="text-slate-600 mt-1">
            Cobre facturas pendientes creadas desde el módulo <strong>Facturación</strong> y consulte las ya cobradas
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'cobradas' ? 'default' : 'outline'}
            className={activeTab === 'cobradas' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            onClick={goToCobradas}
            data-testid="tab-cobradas-btn"
          >
            <CheckCircle2 size={16} className="mr-2" />
            Cobradas ({paidInvoices.length})
          </Button>
          <Button
            variant={activeTab === 'pendientes' ? 'default' : 'outline'}
            className={activeTab === 'pendientes' ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-400 text-amber-700 hover:bg-amber-50'}
            onClick={goToPendientes}
            data-testid="tab-pendientes-btn"
          >
            <Clock size={16} className="mr-2" />
            Cobrar
            {pendingInvoices.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-white text-amber-700" data-testid="pending-count-badge">
                {pendingInvoices.length}
              </span>
            )}
          </Button>
        </div>
      </div>

      {!currentRegister && activeTab === 'pendientes' && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="no-register-alert">
          <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={22} />
          <div>
            <p className="font-semibold text-red-800">Caja cerrada</p>
            <p className="text-sm text-red-700">
              Debe <strong>abrir caja</strong> para poder cobrar facturas pendientes.
            </p>
          </div>
        </div>
      )}

      {/* Tab: Cobradas */}
      {activeTab === 'cobradas' && (
        <Card className="border-emerald-200" data-testid="paid-invoices-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 size={24} />
                  Facturas Cobradas
                </CardTitle>
                <CardDescription>Facturas que ya fueron cobradas desde esta pantalla</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchPaidInvoices} disabled={paidLoading} data-testid="refresh-paid-btn">
                {paidLoading ? 'Cargando...' : 'Actualizar'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {paidInvoices.length === 0 ? (
              <div className="text-center py-10 text-slate-500" data-testid="no-paid-invoices">
                <CheckCircle2 size={48} className="mx-auto mb-3 opacity-30" />
                <p>Aún no hay facturas cobradas desde este módulo</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="custom-table" data-testid="paid-invoices-table">
                  <thead>
                    <tr>
                      <th>Factura</th>
                      <th>NCF</th>
                      <th>Fecha Cobro</th>
                      <th>Cliente</th>
                      <th>Cobrado por</th>
                      <th>Pago</th>
                      <th className="text-right">Total</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paidInvoices.map((inv) => (
                      <tr key={inv.id} data-testid={`paid-invoice-row-${inv.id}`}>
                        <td className="font-semibold">{inv.numero_factura}</td>
                        <td className="text-sm">
                          <span className="font-mono">{inv.ncf || '-'}</span>
                          <div className="text-xs text-slate-500">{inv.tipo_ncf}</div>
                        </td>
                        <td className="text-sm">{inv.fecha || '-'}</td>
                        <td>
                          {inv.cliente?.nombre || '-'}
                          {inv.cliente?.rnc && inv.cliente.rnc !== '000000000' && (
                            <div className="text-xs text-slate-500 font-mono">{inv.cliente.rnc}</div>
                          )}
                        </td>
                        <td className="text-sm">{inv.cobrado_por_nombre || '-'}</td>
                        <td className="text-sm">
                          {inv.es_credito ? (
                            <span className="inline-block px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">Crédito</span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">
                              {inv.tipo_pago?.charAt(0).toUpperCase() + inv.tipo_pago?.slice(1)}
                            </span>
                          )}
                        </td>
                        <td className="text-right font-bold text-emerald-700">{formatCurrency(inv.total || 0)}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => printInvoice(inv)}
                            title="Reimprimir"
                            data-testid={`reprint-btn-${inv.id}`}
                          >
                            <Printer size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Pendientes */}
      {activeTab === 'pendientes' && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="pending-payment-invoices-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-amber-900">
                  <Clock size={24} />
                  Facturas Pendientes
                </CardTitle>
                <CardDescription>Seleccione una factura para elegir método de pago, NCF, confirmar e imprimir</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchPendingInvoices} disabled={pendingLoading} data-testid="refresh-pending-btn">
                {pendingLoading ? 'Cargando...' : 'Actualizar'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pendingInvoices.length === 0 ? (
              <div className="text-center py-10 text-slate-500" data-testid="no-pending-invoices">
                <ReceiptText size={48} className="mx-auto mb-3 opacity-30" />
                <p>No hay facturas pendientes de cobro</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="custom-table" data-testid="pending-invoices-table">
                  <thead>
                    <tr>
                      <th>Factura</th>
                      <th>Fecha</th>
                      <th>Cliente</th>
                      <th>Creada por</th>
                      <th className="text-right">Items</th>
                      <th className="text-right">Total</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInvoices.map((inv) => (
                      <tr key={inv.id} data-testid={`pending-invoice-row-${inv.id}`}>
                        <td className="font-semibold">{inv.numero_factura}</td>
                        <td className="text-sm">{inv.fecha || '-'}</td>
                        <td>{inv.cliente?.nombre || '-'}</td>
                        <td className="text-sm">{inv.cajero_nombre}</td>
                        <td className="text-right">{(inv.items || []).length}</td>
                        <td className="text-right font-bold text-amber-700">{formatCurrency(inv.total || 0)}</td>
                        <td>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => openCobroDialog(inv)}
                              disabled={!currentRegister}
                              title={!currentRegister ? 'Debe abrir caja para cobrar' : 'Cobrar esta factura'}
                              data-testid={`cobrar-pending-btn-${inv.id}`}
                            >
                              <Banknote size={14} className="mr-1" />
                              Cobrar
                            </Button>
                            {(user?.role === 'admin' || user?.role === 'superadmin') && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleCancelPending(inv)}
                                data-testid={`cancel-pending-btn-${inv.id}`}
                                title="Cancelar factura (restaura stock)"
                              >
                                <X size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog de Cobro */}
      <Dialog open={cobroDialogOpen} onOpenChange={(open) => { if (!open && !processingCobro) closeCobroDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cobro-pending-dialog">
          <DialogHeader>
            <DialogTitle>Cobrar Factura {selectedInvoice?.numero_factura || ''}</DialogTitle>
            <DialogDescription>
              Complete datos fiscales, método de pago y confirme el cobro
            </DialogDescription>
          </DialogHeader>

          {selectedInvoice && (
            <form onSubmit={handleCompleteCobro} className="space-y-4">
              {/* Resumen */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Items:</span>
                  <span className="font-semibold">{(selectedInvoice.items || []).length}</span>
                </div>
                <div className="flex justify-between text-lg mt-1 pt-2 border-t">
                  <span className="font-bold">TOTAL:</span>
                  <span className="font-bold text-amber-700">{formatCurrency(selectedInvoice.total || 0)}</span>
                </div>
              </div>

              {/* Datos Fiscales */}
              <div className="p-3 rounded-lg border border-slate-200 bg-white space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <FileText size={16} />
                  Datos Fiscales del Comprobante
                </div>

                <div>
                  <Label htmlFor="cobro-tipo-ncf">Tipo de NCF *</Label>
                  <Select
                    value={cobroFormData.tipo_ncf}
                    onValueChange={(v) => setCobroFormData({ ...cobroFormData, tipo_ncf: v })}
                  >
                    <SelectTrigger data-testid="cobro-tipo-ncf-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ncfTypes.map(t => (
                        <SelectItem key={t.code} value={t.code}>
                          {t.code} - {t.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {cobroFormData.tipo_ncf === 'B01' && (
                    <p className="text-xs text-amber-700 mt-1 font-medium">
                      NCF Crédito Fiscal: requiere RNC válido del cliente
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="cobro-cliente-nombre">Nombre del Cliente</Label>
                  <Input
                    id="cobro-cliente-nombre"
                    value={cobroFormData.cliente_nombre}
                    onChange={(e) => setCobroFormData({ ...cobroFormData, cliente_nombre: e.target.value })}
                    placeholder="CONSUMIDOR FINAL"
                    data-testid="cobro-cliente-nombre-input"
                  />
                </div>

                <div>
                  <Label htmlFor="cobro-cliente-rnc">RNC / Cédula del Cliente</Label>
                  <div className="flex gap-2">
                    <Input
                      id="cobro-cliente-rnc"
                      value={cobroFormData.cliente_rnc}
                      onChange={(e) => { setCobroFormData({ ...cobroFormData, cliente_rnc: e.target.value }); setRncPreview(null); }}
                      placeholder="000-0000000-0"
                      data-testid="cobro-cliente-rnc-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRncLookup}
                      disabled={rncLookupLoading}
                      data-testid="cobro-rnc-lookup-btn"
                      title="Consultar RNC en DGII"
                    >
                      <Search size={16} className="mr-1" />
                      {rncLookupLoading ? 'Consultando...' : 'Consultar'}
                    </Button>
                  </div>

                  {rncPreview && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="rnc-preview">
                      <p className="text-sm font-medium text-blue-900">RNC encontrado en DGII</p>
                      <p className="text-sm text-slate-800 mt-1"><strong>{rncPreview.name}</strong></p>
                      <p className="text-xs text-slate-600">Estado: {rncPreview.status}</p>
                      <div className="flex gap-2 mt-2">
                        <Button type="button" size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmRncName} data-testid="confirm-rnc-btn">
                          Usar estos datos
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setRncPreview(null)}>
                          Descartar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Crédito */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Label htmlFor="cobro-credito" className="cursor-pointer">
                  <div className="font-semibold">Venta a crédito</div>
                  <div className="text-xs text-slate-600">Genera cuenta por cobrar al cliente registrado</div>
                </Label>
                <input
                  id="cobro-credito"
                  type="checkbox"
                  checked={cobroFormData.es_credito}
                  onChange={(e) => setCobroFormData({ ...cobroFormData, es_credito: e.target.checked })}
                  className="w-5 h-5"
                  data-testid="cobro-credito-checkbox"
                />
              </div>

              {cobroFormData.es_credito ? (
                <div className="space-y-3">
                  <div>
                    <Label>Cliente registrado con crédito aprobado *</Label>
                    <Select
                      value={cobroFormData.cliente_id}
                      onValueChange={(v) => {
                        const cust = customers.find(c => c.id === v);
                        setCobroFormData(prev => ({
                          ...prev,
                          cliente_id: v,
                          dias_credito: cust?.dias_credito || prev.dias_credito || 30,
                          cliente_nombre: cust?.nombre || prev.cliente_nombre,
                          cliente_rnc: cust?.rnc || cust?.cedula || prev.cliente_rnc,
                        }));
                      }}
                    >
                      <SelectTrigger data-testid="cobro-cliente-select">
                        <SelectValue placeholder="Seleccionar cliente..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customers
                          .filter(c => c.tiene_credito && c.codigo_cliente !== 'CLI-00000001')
                          .map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.nombre} — Disp: {formatCurrency(c.credito_disponible || 0)}
                            </SelectItem>
                          ))}
                        {customers.filter(c => c.tiene_credito && c.codigo_cliente !== 'CLI-00000001').length === 0 && (
                          <SelectItem value="__none__" disabled>
                            No hay clientes con crédito aprobado
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Solo se muestran clientes registrados con crédito aprobado (Consumidor Final excluido)
                    </p>
                  </div>

                  {/* Detalles de crédito del cliente seleccionado */}
                  {(() => {
                    const cust = customers.find(c => c.id === cobroFormData.cliente_id);
                    if (!cust) return null;
                    const total = selectedInvoice.total || 0;
                    const disponible = cust.credito_disponible || 0;
                    const insufficient = disponible < total;
                    return (
                      <div className={`p-3 rounded-lg border ${insufficient ? 'bg-red-50 border-red-300' : 'bg-emerald-50 border-emerald-300'}`} data-testid="cobro-credito-details">
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <div className="text-xs text-slate-500">Límite total</div>
                            <div className="font-semibold">{formatCurrency(cust.limite_credito || 0)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500">Disponible</div>
                            <div className={`font-semibold ${insufficient ? 'text-red-700' : 'text-emerald-700'}`}>
                              {formatCurrency(disponible)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500">Días crédito</div>
                            <div className="font-semibold">{cust.dias_credito || 30}</div>
                          </div>
                        </div>
                        {insufficient && (
                          <p className="mt-2 text-sm text-red-700 font-semibold">
                            ⚠ Crédito insuficiente para esta factura ({formatCurrency(total)})
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  <div>
                    <Label>Días de crédito (para esta factura)</Label>
                    <Input
                      type="number"
                      value={cobroFormData.dias_credito}
                      onChange={(e) => setCobroFormData({ ...cobroFormData, dias_credito: e.target.value })}
                      min="1"
                      data-testid="cobro-dias-credito-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Prellenado con los días configurados en el cliente; puede ajustarse para esta factura
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label>Método de pago *</Label>
                    <Select
                      value={cobroFormData.tipo_pago}
                      onValueChange={(v) => setCobroFormData({ ...cobroFormData, tipo_pago: v })}
                    >
                      <SelectTrigger data-testid="cobro-tipo-pago-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {cobroFormData.tipo_pago === 'efectivo' && (
                    <div>
                      <Label>Monto recibido (RD$) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={cobroFormData.monto_recibido}
                        onChange={(e) => setCobroFormData({ ...cobroFormData, monto_recibido: e.target.value })}
                        placeholder="0.00"
                        data-testid="cobro-monto-recibido-input"
                      />
                      {parseFloat(cobroFormData.monto_recibido || '0') >= (selectedInvoice.total || 0) && (
                        <p className="text-sm text-green-600 mt-1 font-semibold">
                          Cambio: {formatCurrency(parseFloat(cobroFormData.monto_recibido || '0') - (selectedInvoice.total || 0))}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pb-1">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={closeCobroDialog}
                  disabled={processingCobro}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={processingCobro}
                  data-testid="confirm-cobro-btn"
                >
                  <Banknote size={16} className="mr-2" />
                  {processingCobro ? 'Procesando...' : 'Confirmar Cobro'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingPaymentInvoices;
